// @ts-nocheck
/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import req from '@sportninja/common/api/request'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Button } from 'src/components/Button'
import Icon from 'src/components/Icon'
import { Input } from 'src/components/Input'
import { NewModal } from 'src/components/NewModal'
import { SideModal } from 'src/components/SideModal'
import { useMobxStore } from 'src/state'
import { ModalHeader } from './UpgradeModal'
import { NewUserAddressSelector } from 'src/components/NewUserAddressSelector'
import ModernPhoneInput from 'src/components/ts-components/ModernPhoneInput'
import Text from 'src/components/ts-components/Text'
import { CountryAndProvinceInput } from 'src/components/ts-components/CountryAndProvinceInput'
import { Country, useCountries } from '@sportninja/common/hooks/useCountries'
import { Province } from 'src/components/ts-components/ProvinceSelectInput'
import { App } from 'antd'

interface EditContactFormFields {
  fullName: string
  title: string
  email: string
  companyName: string
}

interface ContactFieldErrors extends EditContactFormFields {
  phone: string
}

const initialContactValues: ContactFieldErrors = {
  fullName: '',
  title: '',
  email: '',
  companyName: '',
  phone: '',
}

// Maps API field names to form field names
const apiToFormFieldMap: Record<string, keyof ContactFieldErrors> = {
  phone_number: 'phone',
  full_name: 'fullName',
  email: 'email',
  company_name: 'companyName',
  title: 'title',
}

interface ActionButtonProps {
  iconName: string
  label: string
  onClick: () => void
  removeMargin?: boolean
}

export const ActionButton = ({
  iconName,
  label,
  onClick,
  removeMargin,
}: ActionButtonProps) => (
  <button
    onClick={onClick}
    css={css`
      margin-left: ${removeMargin ? 0 : '20px'};
    `}
  >
    <Icon name={iconName} color={colors.DEFAULT_FLAIR} fontSize={14} />
    <span
      css={css`
        color: ${colors.DEFAULT_FLAIR};
        margin-left: 8px;
        font-size: 15px;
      `}
    >
      {label}
    </span>
  </button>
)

/**
 * Renders and manages the user's general account settings UI (contact info and address).
 *
 * MobX-observed React functional component that displays user contact information
 * and address details with edit modals for updating each section.
 *
 * @returns {JSX.Element} The account details settings panel with edit functionality
 */
export const Details = observer(() => {
  const {
    me: {
      updateAccountAddress,
      fetchAccount,
      state: { account, me },
    },
  } = useMobxStore()
  const { message } = App.useApp()
  const editContactRef = useRef<NewModal>(null)
  const editAddressRef = useRef<NewModal>(null)
  const editAdditionalContactRef = useRef<SideModal>(null)
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] = useState(initialContactValues)

  // Fetch countries for ID mapping
  const countries = useRef<Country[]>([])
  const provinces = useRef<Province[]>([])

  const setContactInfoValues = useCallback(() => {
    return {
      email: account?.email,
      fullName: account?.full_name,
      title: account?.title,
      companyName: account?.company_name,
    }
  }, [account])

  const [phoneNumber, setPhoneNumber] = useState(account?.phone_number ?? '')
  const [phoneCountry, setPhoneCountry] = useState(
    account?.phone_number_country ?? ''
  )
  const [addressCountry, setAddressCountry] = useState(
    account?.address?.country?.iso_3166_2 ?? ''
  )
  const [addressCountryId, setAddressCountryId] = useState<number | undefined>(
    account?.address?.country?.id
  )

  const [addressProvince, setAddressProvince] = useState(
    account?.address?.province?.iso_3166_2 ?? ''
  )
  const [addressProvinceId, setAddressProvinceId] = useState<
    number | undefined
  >(account?.address?.province?.id)

  const {
    control: contactControl,
    handleSubmit: handleContactSubmit,
    reset: resetContactForm,
  } = useForm<EditContactFormFields>({
    defaultValues: {
      ...setContactInfoValues(),
    },
  })

  const {
    control: addressControl,
    handleSubmit: handleAddressSubmit,
    reset: resetAddressForm,
  } = useForm({
    defaultValues: {
      street: account?.address?.street_1 || '',
      city: account?.address?.city || '',
      postalCode: account?.address?.postal_code || '',
    },
  })

  const [contactId, setContactId] = useState('')

  // Sync form values when account data loads or changes
  useEffect(() => {
    console.log('🔄 Details: Syncing form values with account data')
    setPhoneNumber(account?.phone_number ?? '')
    setPhoneCountry(account?.phone_number_country ?? '')
    setAddressCountry(account?.address?.country?.iso_3166_2 ?? '')
    setAddressCountryId(account?.address?.country?.id)
    setAddressProvince(account?.address?.province?.iso_3166_2 ?? '')
    setAddressProvinceId(account?.address?.province?.id)
  }, [account])

  useEffect(() => {
    console.log('🔄 Details: Resetting contact form values')
    resetContactForm({
      email: account?.email,
      fullName: account?.full_name,
      title: account?.title,
      companyName: account?.company_name,
    })
  }, [account, resetContactForm])

  useEffect(() => {
    console.log('🔄 Details: Resetting address form values')
    resetAddressForm({
      street: account?.address?.street_1 || '',
      city: account?.address?.city || '',
      postalCode: account?.address?.postal_code || '',
    })
  }, [account, resetAddressForm])

  const handleEditContact: SubmitHandler<EditContactFormFields> = useCallback(
    async (data) => {
      // Clear previous errors before submission
      setFieldErrors([])
      setCustomError(initialContactValues)

      try {
        const body = {
          company_name: data.companyName,
          email: data.email,
          full_name: data.fullName,
          title: data.title,
          phone_number: phoneNumber,
          phone_number_country: phoneCountry,
        }

        if (body?.phone_number) {
          if (!body?.phone_number_country) {
            body.phone_number_country = 'US'
          }
        }

        if (phoneNumber?.trim().length === 0) {
          delete body.phone_number_country
          delete body.phone_number
        }

        await req(`/account/${account?.uid}/contact`, {
          method: 'PUT',
          body: JSON.stringify(body),
        })

        // Refetch account data to sync UI
        fetchAccount(me?.account_id)
        message.success('Contact Information Updated Successfully')
        editContactRef.current?.closeModal()
      } catch (e: any) {
        if (e?.invalid_fields) {
          // Map API field errors to form state
          const newFieldErrors: string[] = []
          const newCustomErrors: Partial<ContactFieldErrors> = {}

          Object.entries(e.invalid_fields).forEach(([apiField, message]) => {
            const formField = apiToFormFieldMap[apiField] || apiField
            newFieldErrors.push(formField)
            newCustomErrors[formField as keyof ContactFieldErrors] =
              message as string
          })

          setFieldErrors(newFieldErrors)
          setCustomError((prev) => ({ ...prev, ...newCustomErrors }))
        }
      }
    },
    [account?.uid, me?.account_id, phoneNumber, phoneCountry, fetchAccount]
  )

  const handleEditAddress = useCallback(
    (data: { street: string; city: string; postalCode: string }) => {
      console.log('🔄 handleEditAddress: Submitting address information', {
        ...data,
        countryId: addressCountryId,
        provinceId: addressProvinceId,
      })
      editAddressRef.current?.closeModal()

      updateAccountAddress({
        address_street: data.street,
        address_city: data.city,
        address_country_id: addressCountryId,
        address_province_id: addressProvinceId,
        address_postal_code: data.postalCode,
      })
    },
    [addressCountryId, addressProvinceId, updateAccountAddress]
  )

  const onError = (errors: any) => {
    setFieldErrors([])
    setCustomError(initialContactValues)
    setFieldErrors(Object.keys(errors))

    if (errors.email?.type === 'pattern') {
      setCustomError((prev) => ({ ...prev, email: errors.email.message }))
    }

    if (errors.phone?.type === 'pattern') {
      setCustomError((prev) => ({ ...prev, phone: errors.phone.message }))
    }

    if (errors.phone?.type === 'minLength') {
      setCustomError((prev) => ({
        ...prev,
        phone: 'This phone number is invalid',
      }))
    }
  }

  return (
    <>
      <div
        css={css`
          background-color: ${colors.HEADER};
          padding: 16px;
          border-radius: 8px;
          border: 1px solid ${colors.SOFT_STEEL};
          max-width: 1080px;
          margin: 0 auto;
          margin-top: 16px;
        `}
      >
        {/* CONTACT INFO */}
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding-bottom: 16px;
            border-bottom: 1px solid ${colors.SOFT_STEEL};
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <Text variant='heading4' weight='semiBold' color={colors.NEUTRAL_0}>
              CONTACT INFO
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              Manage Your Contact Information.
            </Text>
          </div>

          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
            `}
          >
            {/* <ActionButton
              iconName='plus-square'
              label='Add another contact'
              onClick={() => {}}
            /> */}
            <ActionButton
              iconName='pen'
              label='Edit'
              onClick={() => editContactRef.current?.openModal()}
            />
          </div>
        </div>
        <div
          css={css`
            border-bottom: 1px solid ${colors.SOFT_STEEL};
            padding-bottom: 16px;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-top: 16px;
            `}
          >
            <Input
              fieldName='fullName'
              disabled
              isMockInput
              mockValue={account?.full_name}
              label='Full Name'
            />
            <Input
              fieldName='title'
              disabled
              isMockInput
              mockValue={account?.title}
              label='Title'
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-top: 16px;
            `}
          >
            <ModernPhoneInput
              label='Phone Number *'
              value={account?.phone_number || ''}
              onChange={() => {}} // No-op since disabled
              disabled={true}
            />
            <Input
              fieldName='email'
              disabled
              isMockInput
              mockValue={account?.email}
              label='Email'
              type='email'
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-top: 16px;
            `}
          >
            <Input
              fieldName='companyName'
              disabled
              isMockInput
              mockValue={account?.company_name}
              label='Company Name'
            />
          </div>
        </div>

        {/* ADDRESS */}
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-top: 16px;
          `}
        >
          <Text variant='heading5' weight='regular' color={colors.WHITE}>
            ADDRESS
          </Text>
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <ActionButton
              iconName='pen'
              label='Edit'
              onClick={() => editAddressRef.current?.openModal()}
            />
          </div>
        </div>
        <div
          css={css`
            margin-bottom: 16px;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-top: 16px;
            `}
          >
            <Input
              fieldName='street'
              disabled
              isMockInput
              mockValue={account?.address?.street_1}
              label='Street'
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-top: 16px;
            `}
          >
            <Input
              fieldName='city'
              disabled
              isMockInput
              mockValue={account?.address?.city}
              label='City'
            />
            <Input
              fieldName='state'
              disabled
              isMockInput
              mockValue={account?.address?.province?.name}
              label='State/Province'
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-top: 16px;
            `}
          >
            <Input
              fieldName='zip'
              disabled
              isMockInput
              mockValue={account?.address?.postal_code}
              label='ZIP/Postal Code'
            />
            <Input
              fieldName='country'
              disabled
              isMockInput
              mockValue={account?.address?.country?.name}
              label='Country'
            />
          </div>
        </div>
      </div>
      {/* MODALS */}
      <NewModal ref={editContactRef}>
        <div
          css={css`
            width: 512px;
            padding: 16px;
            background: no-repeat fixed linear-gradient(#282e38, #181a1d);
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            max-height: 80vh;
            overflow-y: auto;
          `}
        >
          <ModalHeader
            onCloseClick={() => {
              setFieldErrors([])
              setCustomError(initialContactValues)
              editContactRef.current?.closeModal()
            }}
            hideSupportButton
            title='Edit Contact'
          />
          <div>
            <div
              css={css`
                margin-top: 16px;
              `}
            >
              <Input
                control={contactControl}
                fieldName='fullName'
                label='Full Name'
                required
                invalidMessage={
                  customError.fullName || 'This field is mandatory'
                }
                invalid={fieldErrors.includes('fullName')}
              />
            </div>
            <div
              css={css`
                margin-top: 8px;
              `}
            >
              <Input
                control={contactControl}
                fieldName='title'
                label='Title'
                required
                invalidMessage={customError.title || 'This field is mandatory'}
                invalid={fieldErrors.includes('title')}
              />
            </div>
            <div
              css={css`
                margin-top: 8px;
                margin-bottom: 20px;
              `}
            >
              <ModernPhoneInput
                label='Phone Number *'
                value={phoneNumber}
                onChange={(value) => setPhoneNumber(value)}
                onCountryChange={(country) => setPhoneCountry(country || '')}
                tabIndex={1}
                validateOnBlur={false}
                error={
                  fieldErrors.includes('phone')
                    ? customError.phone || 'Invalid phone number'
                    : undefined
                }
                onValidationChange={(isValid) => {
                  if (!isValid) {
                    setFieldErrors((prev) => [...new Set([...prev, 'phone'])])
                    setCustomError((prev) => ({
                      ...prev,
                      phone: 'Invalid phone number',
                    }))
                  } else {
                    setFieldErrors((prev) => prev.filter((f) => f !== 'phone'))
                    setCustomError((prev) => ({ ...prev, phone: '' }))
                  }
                }}
              />
            </div>
            <div
              css={css`
                margin-top: 8px;
              `}
            >
              <Input
                control={contactControl}
                fieldName='email'
                label='Email'
                type='email'
                required
                disabled
                invalidMessage={customError.email || 'This field is mandatory'}
                invalid={fieldErrors.includes('email')}
              />
            </div>
            <div
              css={css`
                margin-top: 8px;
              `}
            >
              <Input
                control={contactControl}
                fieldName='companyName'
                label='Company Name'
                required
              />
            </div>
          </div>
          <div
            css={css`
              margin-top: 32px;
            `}
          >
            <Button
              label='Submit'
              onClick={handleContactSubmit(handleEditContact, onError)}
            />
          </div>
        </div>
      </NewModal>
      <NewModal ref={editAddressRef}>
        <div
          css={css`
            width: 512px;
            padding: 16px;
            background: no-repeat fixed linear-gradient(#282e38, #181a1d);
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            max-height: 80vh;
            overflow-y: auto;
          `}
        >
          <ModalHeader
            onCloseClick={() => {
              editAddressRef.current?.closeModal()
            }}
            hideSupportButton
            title='Edit Address'
          />
          <div
            css={css`
              margin-top: 16px;
            `}
          >
            <Input
              control={addressControl}
              fieldName='street'
              label='Street'
              required
            />
          </div>
          <div
            css={css`
              margin-top: 8px;
            `}
          >
            <Input
              control={addressControl}
              fieldName='city'
              label='City'
              required
            />
          </div>
          <div
            css={css`
              margin-top: 8px;
            `}
          >
            <CountryAndProvinceInput
              allowClear={true}
              countriesRef={countries}
              provincesRef={provinces}
              countryValue={addressCountry}
              provinceValue={addressProvince}
              onCountryChange={(value) => {
                console.log('🔄 Details: Country changed to', value)
                setAddressCountry(value || '')
                // Map ISO code to country ID
                const country = countries?.current?.find(
                  (c) => c.iso_3166_2 === value
                )
                setAddressCountryId(country?.id)
                console.log('🔄 Details: Country ID set to', country?.id)
              }}
              onProvinceChange={(value) => {
                console.log('🔄 Details: Province changed to', value)
                setAddressProvince(value || '')
                // Map ISO code to province ID
                const province = provinces?.current?.find(
                  (p) => p.iso_3166_2 === value
                )
                setAddressProvinceId(province?.id)
                console.log('🔄 Details: Province ID set to', province?.id)
              }}
              valueMode='iso_code'
              provinceValueMode='iso_code'
              labels={{
                country: 'Country',
                province: 'State/Province',
              }}
              placeholders={{
                country: 'Select a country',
                province: 'Select state/province',
              }}
              customErrors={{
                country: customError.country,
                province: customError.province,
              }}
              fieldErrors={{
                country: fieldErrors.includes('country'),
                province: fieldErrors.includes('province'),
              }}
              requiredFields={{
                country: true,
                province: true,
              }}
            />
          </div>
          <div
            css={css`
              margin-top: 8px;
            `}
          >
            <Input
              control={addressControl}
              fieldName='postalCode'
              label='ZIP/Postal Code'
              required
            />
          </div>
          <div
            css={css`
              margin-top: 32px;
            `}
          >
            <Button
              label='Submit'
              onClick={handleAddressSubmit(handleEditAddress)}
              disabled={!addressCountryId || !addressProvinceId}
            />
          </div>
        </div>
      </NewModal>

      <SideModal ref={editAdditionalContactRef} title='Edit Additional Contact'>
        <p>id: {contactId}</p>
      </SideModal>
    </>
  )
})
